import { observable, makeObservable, action, computed, reaction, when } from 'mobx';
import { FieldDesc, FormStore } from '../FormStore'
import { Validators } from '../FormValidator';
import { ValueObj } from '../data/FormData';
import { v4 as uuid } from "uuid";
import { get } from 'lodash'
import { convertField } from '../../common'

function debounce<T extends (...args: any[]) => void>(func: T, delay: number): (...args: Parameters<T>) => void {
  let timerId: ReturnType<typeof setTimeout> | undefined;
  return function(this: any, ...args: Parameters<T>) {
    const context = this;

    clearTimeout(timerId);
    timerId = setTimeout(() => {
      func.apply(context, args);
    }, delay);
  }
}
export interface validateDesc {
  required: boolean,
  type?: string,
  max?: number,
  min?: number,
  minLength?: number,
  maxLength?: number
}

export const buildValidators = (fieldDesc: validateDesc): Validators => {
  const { required, type, max, min, minLength, maxLength } = fieldDesc
  const validators: Validators = []
  if (required) validators.push(['required'])
  if (type) validators.push(['type', type])
  if (max || min) validators.push(['range', typeof min === undefined ? -Infinity : min, typeof max === undefined ? Infinity : max])
  if (maxLength || minLength) validators.push(['length', typeof minLength === undefined ? -Infinity : minLength, typeof maxLength === undefined ? Infinity : maxLength])
  return validators
}

const mobxReg = /^mobx:\/\//

export type HOOKS = {
  [hookName: string]: {
    [handler: string] : HOOK
  }
}

type HOOK = (params:any) => void | Promise<any> | boolean
export class BaseField {
  formStore: FormStore
  fieldDesc: FieldDesc
  fieldAlias: string
  field?: string
  fieldName?: string
  controlType?: string
  errs?: string[] // 错误提示
  visible?: boolean // 显示
  readOnly?: boolean //只读
  required?: boolean // 必填
  disabled?: boolean //禁用
  characterId?: string;
  stateType?: string; //character
  bizType?: string;
  defaultValue?: ValueObj
  isValidate?: boolean
  hooks?: HOOKS
  uncopyable?: boolean
  constructor(fieldDesc: FieldDesc, formStore: FormStore) {
    this.formStore = formStore
    this.fieldDesc = fieldDesc
    this.fieldAlias = fieldDesc.fieldAlias
    this.bizType = fieldDesc.bizType
    this.fieldName = fieldDesc.caption
    this.isValidate = true
    this.defaultValue = fieldDesc.defaultValue
    this.characterId = fieldDesc.characterId
    this.stateType = fieldDesc.stateType
    this.controlType= fieldDesc.controlType
    this.hooks = fieldDesc.hooks
    this.errs = []
    this.readOnly = fieldDesc.readOnly
    this.visible = fieldDesc.visible
    this.required = fieldDesc.required
    this.disabled = fieldDesc.disabled
    // 字段处理需要最后处理
    this.field = this.autoFieldPretreatment(fieldDesc.field)
    this.uncopyable = fieldDesc.uncopyable
    makeObservable(this, {
      errs: observable,
      visible: observable,
      readOnly: observable,
      disabled: observable,
      required: observable,
      validators: computed,
      hasValue: computed,
      isBrowse: computed,
      isVisible: computed,
      isDisabled: computed,
      value: computed,
      hasErr: computed,
      errorTips: computed,
      contentStyle: computed,
      setValue: action.bound,
      setVisible: action.bound,
      setReadOnly: action.bound,
      setDisabled: action.bound,
      setRequired: action.bound,
      validate: action.bound,
      setHook: action.bound,
      removeHook: action.bound,
      flowFieldAuth: computed
      // mergeProps: action.bound
    })

    if (this.formStore?.rootStore?.pageStore?.bRunInDesigner || Boolean(this.formStore.isAdd && !this.hasValue && this.defaultValue !== undefined)) {
      this.setValue(this.getMobxProperty(this.defaultValue))
    }

    // 用datasource构造field时, form状态是init, 如果状态设置为add, 需要设置默认值
    reaction(
      () => this.formStore.isAdd,
      (isAdd: any) => {
        if (Boolean(isAdd && !this.hasValue && this.defaultValue !== undefined)) {
          this.setValue(this.getMobxProperty(this.defaultValue))
        }
      }
    )
  }

  get isFilter() {
    return this.controlType?.startsWith('Filter')
  }

  get value () {
    return this.getValue()
  }

  get validators () {
    const required = this.getMobxProperty(this.required)
    return buildValidators({required: (required === true || required === 'true') ? true : false})
  }

  /**
   *
   * @param field 参照带入值是一个普通的字段，store配置的field是 aaa.bbb 这种形式，但是后端返回的数据是 aaa_bbb这种形式
   * @returns
   */
  private autoFieldPretreatment(field?: string){
    if(this.bizType == 'quote' && field){
      return convertField(field, !this.isCharacter())
    }
    if(this.stateType && !field){
      return this.fieldAlias
    }
    return field
  }

  getValue () {
    const key = this.getField()
    if (this.isCharacter()) {
      return get(this.formStore.data, key)
    }
    return this.formStore.data[key] ?? this.formStore.data[this.fieldAlias] //兼容老数据，老数据存在问题字段不匹配
  }
  public getField() {
    return this.field || this.fieldAlias
  }
  public isCharacter () {
    return !! (this.characterId || this.stateType)
  }

  setValue (value?: string | number | ValueObj) {
    this.formStore.setTrueValue(this.getField(), value, this.isCharacter())
    this.validate()
  }

  setHook (hookName: string, hook: HOOK) {
    if (!this.hooks) this.hooks = {}
    if (!this.hooks[hookName]) this.hooks[hookName] = {}
    const hookHandler = uuid()
    this.hooks[hookName][hookHandler] = hook
    return hookHandler
  }

  removeHook (hookName: string, hookHandler: string) {
    delete this.hooks?.[hookName]?.[hookHandler]
  }

  async useHook (hookName: string, params: any) {
    if (this.hooks?.[hookName]) {
      const hooks = Object.values(this.hooks[hookName])
      const res = await Promise.all(hooks.map(async (beforeHook) => {
        return await beforeHook(params)
      }))
      return !res.some(resValue => resValue === false)
    } else {
      return true
    }
  }

  clear () {
    if(this.hasValue) this.formStore.setTrueValue(this.getField(), undefined, this.isCharacter())
  }

  setVisible(visible: boolean) {
    this.visible = visible
  }

  setReadOnly(readOnly: boolean) {
    this.readOnly = readOnly
  }

  setDisabled(disabled: boolean) {
    this.disabled = disabled
  }

  setRequired(required: boolean) {
    this.required = required
  }

  getMobxProperty = (property: any) => {
    // @ts-ignore
    const rootStore = this.formStore.rootStore
    if (typeof property === "string" && mobxReg.test(property)) {
      try {
        return eval(property.replace(mobxReg, ''));
      } catch (error) {
        console.log("error", error);
      }
    } 
    return property 
    // return typeof (property) === 'string' && mobxReg.test(property)
    // ? eval(property.replace(mobxReg, ''))
    // : property
  }

  get hasValue () {
    return Boolean(this.getValue()?.toString())
  }

  get isBrowse () {
    return this.formStore.isBrowse || this.readOnly
  }

  get isVisible () {
    if (this.flowFieldAuth && !this.flowFieldAuth.visible) return false
    if (this.formStore.showRequiredFields) {
      const required = this.getMobxProperty(this.required)
      return Boolean(required)
    } else {
      return this.getMobxProperty(this.visible)
    }
  }

  get isDisabled () {
    // query里有transtype参数的话，交易类型不能编辑
    if (this.fieldAlias === this.formStore.transTypeField && this.formStore.rootStore.pageStore.query.transtype) return true
    if (this.flowFieldAuth && !this.flowFieldAuth.editable) return true
    if (this.formStore.masterOrgField) {
      // 当前字段是主组织字段，只有在新增态可以编辑
      if (this.fieldAlias === this.formStore.masterOrgField) return !this.formStore.isAdd
      // 如果主组织没有值，其他字段都禁用, 否则返回字段的disabled
      return !this.formStore.masterOrgFieldValue ? true : this.disabled
    } else {
      return this.disabled
    }
  }

  get hasErr () {
    return Boolean(this.errs?.length)
  }

  validate = debounce(() => {
    const errs = this.formStore.formValidator.validate([{fieldAlias: this.fieldAlias, fieldName: this.fieldName, value: this.getValue(), validators: this.validators }])
    this.errs = errs.errMsgs
    return this.errs
  }, 600)

  validateAtOnce = () => {
    const errs = this.formStore.formValidator.validate([{fieldAlias: this.fieldAlias, fieldName: this.fieldName, value: this.getValue(), validators: this.validators }])
    this.errs = errs.errMsgs
    return this.errs
  }

  get errorTips() {
    return this.errs?.length === 0 ? '' : this.errs?.[0]
  }

  get contentStyle() {
    // LDM-2267 取消预览态 字体加粗
    // return this.formStore.isBrowse ? {fontWeight: 'bold'} : {}
    return {}
  }

  get flowFieldAuth() {
    if (!this.field) return
    // 找到主表的formStore
    let form = this.formStore
    // 如果是移动端子表的formStore的话，需要通过tableStore定位到主表的formStore
    if (this.formStore.tableStoreKey) {
      const table = this.formStore.rootStore[this.formStore.tableStoreKey]
      form = this.formStore.rootStore[table.formStoreKey]
    }
    // 没有流程实例id，或者驳回到制单的状态，或者没有配置过流程字段权限，就不用管字段权限
    if (!form.procinstId || form.verifyState == 4 || !form.workFlowStore.currentTaskAuth) return

    const defaultAuth = { visible: true, editable: false }
    if (this.formStore.tableStoreKey) {
      // 移动端子表编辑页的情况
      // 特征字段
      if (this.characterId) {
        return get(form.workFlowStore.currentTaskAuth[this.formStore.dataSource], this.field) || defaultAuth
      }
      return form.workFlowStore.currentTaskAuth[this.formStore.dataSource]?.[this.field] || defaultAuth
    } else {
      // 主表的情况
      // 特征字段
      if (this.characterId) {
        return get(form.workFlowStore.currentTaskAuth, this.field) || defaultAuth
      }
      return form.workFlowStore.currentTaskAuth.mainTable?.[this.field] || defaultAuth
    }
  }
}
